import NiceModal, { useModal } from "@ebay/nice-modal-react";
import clsx from "clsx";
import withSearch, { SearchProps } from "hocs/withSearch";
import { forwardRef, memo, useState } from "react";
import AnimateHeight from "react-animate-height";
import { FormProvider, useForm } from "react-hook-form";
import ReactModal from "react-modal";

import { ReactComponent as ChevronDownIcon } from "assets/icons/chevron-down.svg";
import { ReactComponent as CloseIcon } from "assets/icons/close.svg";
import { ReactComponent as SearchIcon } from "assets/icons/search.svg";
import Box from "components/Box";
import Empty from "components/Empty";
import Pagination from "components/Pagination";
import Select from "components/Select";
import { useGetNotices } from "hooks/apis/notice";
import { Notice } from "types/types";
import { faqTypesOptions } from "utils/const";

const size = 20;

type FormValues = {
  category: string;
  searchText: string;
  page: number;
};

const Faq = () => {
  const modal = useModal();
  const methods = useForm<FormValues>();
  const category = methods.watch("category");
  const searchText = methods.watch("searchText");
  const page = methods.watch("page");

  const { data, totalItems, isFetching } = useGetNotices({
    type: "faq",
    category,
    searchText,
    page,
    size,
  });

  const closeModal = () => {
    modal.hide();
    modal.reject();
  };

  return (
    <ReactModal
      overlayClassName="overflow-auto flex fixed size-full top-0 left-0 z-30"
      className="bg-[rgba(0,0,0,0.2)] min-w-[1280px] min-h-[720px] flex justify-center items-center size-full outline-none"
      isOpen={modal.visible}
      onRequestClose={closeModal}
      onAfterClose={() => {
        modal.remove();
      }}
    >
      <FormProvider {...methods}>
        <main className="bg-white flex flex-col size-[50rem] h900:h-[43.75rem] h800:h-[37.5rem] p-[1.875rem] rounded-md">
          <header className="flex justify-end w-full relative">
            <h2 className="absolute text-center inset-0 justify-self-center self-center font-bold text-24 ">
              자주하는 질문
            </h2>
            <button
              className="relative z-10"
              type="button"
              onClick={closeModal}
            >
              <CloseIcon className="size-[2.25rem]" />
            </button>
          </header>
          <section className="flex flex-col flex-1 overflow-hidden">
            <div className="flex justify-between mt-[4.375rem]">
              <Select
                name="category"
                options={faqTypesOptions}
                size="bigger"
                className="!w-[9.688rem] !border-secondary"
                border
              />
              <div className="w-[25rem]">
                <EnhancedSearchField />
              </div>
            </div>
            <hr className="my-5 border-[#A5A5A5]" />
            <div className="flex-1 flex flex-col overflow-auto">
              {data?.length ? (
                <ul className="border-b border-gray">
                  {data.map((item) => (
                    <Disclosure key={item.id} {...item} />
                  ))}
                </ul>
              ) : (
                <Empty show={!isFetching && data && !data.length} />
              )}
            </div>
            <Pagination total={totalItems} />
          </section>
        </main>
      </FormProvider>
    </ReactModal>
  );
};

const SearchField = forwardRef<HTMLInputElement, SearchProps>(
  ({ onKeyDown, onChange, onSearch }, ref) => {
    return (
      <Box className="flex-1 flex items-center gap-[0.625rem] !pl-5 !pr-[1.375rem] !py-[0.938rem] !border-secondary">
        <input
          ref={ref}
          type="text"
          className="placeholder:text-secondary w-full text-16 outline-none"
          placeholder="키워드를 입력해주세요"
          onKeyDown={onKeyDown}
          onChange={onChange}
        />
        <button type="button" onClick={onSearch} className="shrink-0">
          <SearchIcon className="size-6" />
        </button>
      </Box>
    );
  },
);

SearchField.displayName = "SearchField";

const EnhancedSearchField = withSearch(SearchField);

const Disclosure = (item: Notice) => {
  const [height, setHeight] = useState<0 | "auto">(0);

  const toggleHeight = () => {
    setHeight((h) => (h ? 0 : "auto"));
  };

  return (
    <li className="w-full">
      <button
        onClick={toggleHeight}
        type="button"
        className="w-full flex justify-between items-center gap-[0.625rem] text-left text-16 font-medium p-5 border-t border-gray"
      >
        <p>
          [{item.type}] {item.title}
        </p>
        <ChevronDownIcon
          className={clsx("size-[0.813rem]", {
            "-rotate-180 duration-300": height === "auto",
          })}
        />
      </button>
      <AnimateHeight height={height} duration={300}>
        <div className="text-14 leading-normal px-5 pb-[0.438rem]">
          <div
            dangerouslySetInnerHTML={{
              __html: item.content,
            }}
          />
        </div>
      </AnimateHeight>
    </li>
  );
};

const FaqModal = NiceModal.create(memo(Faq));
FaqModal.displayName = "FaqModal";

export default FaqModal;
